import { useEffect, useState } from 'react';
import he from 'he';
import { formatEventDate, parseAcfDate, type EventCardItem } from '@/pages/events/eventsHelpers';

const EVENTS_ENDPOINT = 'https://blog.disgenet.com/wp-json/wp/v2/events';
const PER_PAGE = 100;
const MAX_PAGES = 20;

type WordPressRenderedField = {
  rendered?: string;
};

type AcfThumbnail = {
  url?: string;
  alt?: string;
  title?: string;
  sizes?: Record<string, string | number>;
};

type WordPressEvent = {
  id: number;
  title?: WordPressRenderedField;
  acf?: {
    title?: string;
    description?: string;
    date?: string;
    place?: string;
    type?: string | { label?: string; value?: string } | null;
    link?: string;
    thumbnail?: AcfThumbnail | false | null;
  } | null;
};

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getValidHttpsUrl = (value?: string | null) => {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

type AcfType = string | { label?: string; value?: string } | null | undefined;

const pickAcfType = (value: AcfType) => {
  if (!value) return '';
  if (typeof value === 'string') return stripHtml(value);
  return stripHtml(value.label || value.value || '');
};

const pickThumbnailUrl = (thumbnail: AcfThumbnail | false | null | undefined) => {
  if (!thumbnail || typeof thumbnail !== 'object') return null;
  const sizes = thumbnail.sizes ?? {};
  const candidates = [sizes.medium_large, sizes.large, sizes.medium, thumbnail.url];
  return (
    candidates.find((value): value is string => typeof value === 'string' && value.startsWith('https://')) ??
    null
  );
};

const mapEvent = (item: WordPressEvent): EventCardItem => {
  const acf = item.acf ?? {};
  const rawTitle = acf.title || item.title?.rendered || 'Untitled event';
  const title = he.decode(stripHtml(rawTitle));
  const description = he.decode(stripHtml(acf.description || ''));
  const dateValue = parseAcfDate(acf.date || '');
  const thumbnail = acf.thumbnail && typeof acf.thumbnail === 'object' ? acf.thumbnail : null;

  return {
    id: item.id,
    title,
    description,
    dateLabel: dateValue ? formatEventDate(dateValue) : (acf.date || '').trim(),
    dateValue,
    place: stripHtml(acf.place || ''),
    eventType: pickAcfType(acf.type),
    link: getValidHttpsUrl(acf.link || null),
    thumbnailUrl: pickThumbnailUrl(thumbnail),
    thumbnailAlt: he.decode(stripHtml(thumbnail?.alt || thumbnail?.title || title)),
    year: dateValue ? String(dateValue.getFullYear()) : '',
  };
};

const fetchEventsPage = async (page: number) => {
  const url = `${EVENTS_ENDPOINT}?per_page=${PER_PAGE}&page=${page}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Events request failed');
  }

  const parsed: unknown = await response.json();
  if (!Array.isArray(parsed)) {
    throw new Error('Unexpected events response');
  }

  const totalPagesHeader = response.headers.get('X-WP-TotalPages');
  const totalPages = totalPagesHeader ? Number(totalPagesHeader) : parsed.length < PER_PAGE ? page : page + 1;

  return {
    items: parsed as WordPressEvent[],
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : page,
  };
};

export const useEvents = () => {
  const [events, setEvents] = useState<EventCardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchEvents = async () => {
      setLoading(true);
      setError(null);

      try {
        const collected: WordPressEvent[] = [];
        let page = 1;
        let totalPages = 1;

        do {
          const result = await fetchEventsPage(page);
          collected.push(...result.items);
          totalPages = Math.min(result.totalPages, MAX_PAGES);
          page += 1;
        } while (page <= totalPages);

        if (cancelled) return;
        setEvents(collected.map(mapEvent));
      } catch (fetchError) {
        if (cancelled) return;
        setError(fetchError instanceof Error ? fetchError.message : 'Failed to load events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  return { events, loading, error };
};
